import type { ReactNode } from "react";

import { useRouter } from "next/router";

import RocketLoading from "@/components/atoms/loading";
import { getCurrentAccountFromSettings } from "@/hooks/use-current-account";
import { useLegacyRouteRedirect } from "@/hooks/use-legacy-route-redirect";
import { getLegacyRouteRedirectTarget } from "@/lib/legacy-route-transition";
import { isOfficialTestAccountSession } from "@/lib/test-account-environment";
import AuthService from "@/services/auth";

function LegacyRouteGate({ children, target }: { children: ReactNode; target: string }) {
  const { retry, transition } = useLegacyRouteRedirect(target);

  if (transition === "failed") {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-white px-6 text-center"
        data-legacy-route-transition="failed"
        role="alert"
      >
        <div className="flex max-w-md flex-col items-center gap-4">
          <p className="m-0 text-base text-slate-700">
            Não foi possível abrir a nova experiência do Manager.
          </p>
          <button
            className="rounded-lg bg-primary px-4 py-2 font-semibold text-primary-foreground"
            onClick={retry}
            type="button"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (transition !== "render") {
    return (
      <div aria-label="Carregando a experiência do Manager" data-legacy-route-transition={transition} role="status">
        <RocketLoading />
      </div>
    );
  }

  return children;
}

export function LegacyRouteTransitionBoundary({ children }: { children: ReactNode }) {
  const router = useRouter();
  const target = getLegacyRouteRedirectTarget(router.pathname);

  // A conta oficial precisa abrir rotas legadas para QA. O helper vincula os
  // identificadores canônicos do cache aos claims do token atual; montar o gate
  // depois disso revalidaria a mesma conta antes do shell, serializando duas
  // cargas de settings e deixando páginas como /gift aparentemente vazias.
  if (!target) return children;
  if (isOfficialTestAccountSession(getCurrentAccountFromSettings(), AuthService.isLogged())) {
    return children;
  }

  return <LegacyRouteGate target={target}>{children}</LegacyRouteGate>;
}
